import React from 'react';
import { db } from '@/js/external-js/config/main_config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faListTree,
  faTriangleExclamation,
  faBookOpen,
  faDatabase,
  faUserShield,
  faCabinetFiling, // Default icon for fallback
  faTruckContainer, // DED-1535: Supplier icon
} from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { getEnabledModules } from '@/js/external-js/variableModules/utils/EnabledModulesManager';

// Define the register item type
export interface RegisterItem {
  title: string;
  shortTitle?: string;
  url: string;
  subtitle: string;
  icon: React.ComponentType<any>;
  routeName?: string; // Route name for SPA navigation
}

// Utility: Create a stable FontAwesomeIcon component with the given icon
const createIconComponent = (icon: any): React.ComponentType<any> => {
  return React.memo(function IconComponent(props: any) {
    if (!icon) {
      console.error('Undefined icon passed to createIconComponent');
      return <FontAwesomeIcon icon={faCabinetFiling} {...props} />;
    }
    return <FontAwesomeIcon icon={icon} {...props} />;
  });
};

// Map of register URLs to their corresponding icons
const registerIconMap: Record<string, any> = {
  'asset-register': faListTree,
  'assetregister.html': faListTree,
  'supplier-register': faTruckContainer, // DED-1535: Supplier Register icon
  'risk-register': faTriangleExclamation,
  'ismsregister.html': faBookOpen,
  'dataRetentionSchedule.html': faDatabase,
  'privacyRequestsTracker.html': faUserShield,
  'auditcentre/incidents': faTriangleExclamation, // DED-1141: Incident Register
};

export interface GetRegisterItemsOptions {
  isEnterprise?: boolean;
}

/**
 * Gets the register items for the sidebar based on enabled standards
 * @param {string} tenancyId The active tenancy ID
 * @param {GetRegisterItemsOptions} [options] Options object
 * @param {boolean} [options.isEnterprise=false] Whether the customer is an enterprise
   * @returns {Promise<RegisterItem[]>} Promise resolving to an array of register items
   */
export const getRegisterItems = async (tenancyId: string, options: GetRegisterItemsOptions = {}): Promise<RegisterItem[]> => {
  if (!tenancyId) return [];

  const { isEnterprise = false } = options;

  // Check enabled modules for tenancy
  const enabledModules = await getEnabledModules();

  // Check if SMB 1001 is the only enabled module
  const isSMB1001Only = enabledModules.size === 1 && (enabledModules.values().next().value.toLowerCase() === 'smb 1001' ||enabledModules.values().next().value.toLowerCase() === 'smb1001');

  try {
    const items: RegisterItem[] = [];

    // Get company settings to check which modules are enabled
    const companySettings = await db
      .collection('customers')
      .doc(tenancyId)
      .collection('companySettings')
      .doc('complianceModules')
      .get();

    // Get customer plan
    const customerDoc = await db
      .collection('customers')
      .doc(tenancyId)
      .get();

    const customerPlan = customerDoc.data()?.customerPlan;
    const iso27701Enabled = companySettings.data()?.iso27701?.Enabled?.value === true;
    // DED-1511: Also check for Privacy Act module being enabled
    const privacyActEnabled = companySettings.data()?.privacyAct?.Enabled?.value === true;
    // Check if any privacy-related module is enabled
    const hasPrivacyModule = iso27701Enabled || privacyActEnabled || enabledModules.has('privacyAct') || enabledModules.has('Privacy Act');

    // Create cards for default plan
    if (customerPlan === undefined || customerPlan === 'default' || customerPlan === 'Business Plan' || customerPlan === 'Enterprise Plan') {
      // DED-1535: Split Asset and Supplier into separate registers
      items.push({
        title: 'Asset Register',
        shortTitle: 'Asset',
        url: '/asset-register',
        subtitle: "Manage your company's hardware, software, and other assets",
        icon: createIconComponent(registerIconMap['asset-register']),
        routeName: 'asset-register', // Add routeName for SPA navigation
      });

      items.push({
        title: 'Supplier Register',
        shortTitle: 'Supplier',
        url: '/supplier-register',
        subtitle: "Manage your suppliers and track shared data",
        icon: createIconComponent(registerIconMap['supplier-register']),
        routeName: 'supplier-register', // Add routeName for SPA navigation
      });

      items.push({
        title: 'Risk Register',
        shortTitle: 'Risk',
        url: '/risk-register',
        subtitle: "Manage your company's risks",
        icon: createIconComponent(registerIconMap['risk-register']),
        routeName: 'risk-register', // Add routeName for SPA navigation
      });

      // if (!isEnterprise) {
      //   items.push({
      //     title: 'ISMS Register',
      //     shortTitle: 'ISMS',
      //     url: '/ismsregister.html',
      //     subtitle: 'Manage incidents, changes, and non-conformities in one place.',
      //     icon: createIconComponent(registerIconMap['ismsregister.html']),
      //   });
      // }
      items.push({
        title: 'Data Register',
        shortTitle: 'Data',
        url: '/dataRegister',
        routeName: 'dataRegister', // Add routeName for SPA navigation
        subtitle: 'Document data types, purposes, and retention periods.',
        icon: createIconComponent(registerIconMap['dataRetentionSchedule.html']),
      });


      // Check if any privacy module is enabled (ISO 27701 or Privacy Act)
      if (hasPrivacyModule) {
        items.push({
          title: 'Data Privacy Requests',
          shortTitle: 'Privacy',
          url: '/privacy-request-register',
          routeName: 'privacy-request-register', // Add routeName for SPA navigation
          subtitle: 'Manage data privacy requests in one place.',
          icon: createIconComponent(registerIconMap['privacyRequestsTracker.html']),
        });
      }

      // DED-1141: Add Incident Register to Registers section
      items.push({
        title: 'Incident Register',
        shortTitle: 'Incidents',
        url: '/auditcentre/incidents',
        routeName: 'auditcentre.incidents',
        subtitle: 'Track and manage security incidents.',
        icon: createIconComponent(registerIconMap['auditcentre/incidents']),
      });
    } else {
      // Create cards for customers with a defined plan (e.g., Starter Plan)
      // DED-1535: Use the new dedicated supplier register route
      items.push({
        title: 'Supplier Register',
        shortTitle: 'Supplier',
        url: '/supplier-register',
        routeName: 'supplier-register',
        subtitle: 'Keep track of your suppliers and shared data.',
        icon: createIconComponent(registerIconMap['supplier-register']),
      });

      items.push({
        title: 'Data Register',
        shortTitle: 'Data',
        url: '/dataRegister',
        routeName: 'dataRegister', // Add routeName for SPA navigation
        subtitle: 'Document data types, purposes, and retention periods.',
        icon: createIconComponent(registerIconMap['dataRetentionSchedule.html']),
      });

      // DED-1511: Show privacy register if any privacy module is enabled or if not SMB1001 only
      if (hasPrivacyModule || !isSMB1001Only) {
        items.push({
          title: 'Data Privacy Requests',
          shortTitle: 'Privacy',
          url: '/privacy-request-register',
          routeName: 'privacy-request-register', // Add routeName for SPA navigation
          subtitle: 'Manage data privacy requests in one place.',
          icon: createIconComponent(registerIconMap['privacyRequestsTracker.html']),
        });
      }

      // DED-1141: Add Incident Register to Registers section for starter plans
      items.push({
        title: 'Incident Register',
        shortTitle: 'Incidents',
        url: '/auditcentre/incidents',
        routeName: 'auditcentre.incidents',
        subtitle: 'Track and manage security incidents.',
        icon: createIconComponent(registerIconMap['auditcentre/incidents']),
      });
    }

    return items;
  } catch (error) {
    console.error('Error fetching register items:', error);
    return [];
  }
};

// Cache keys for register items
const REGISTER_ITEMS_CACHE_KEY = 'register_items_cache_v1';

// Utility functions for caching register items
export function loadCachedRegisterItems(tenancyId: string): RegisterItem[] {
  try {
    const cacheKey = `${REGISTER_ITEMS_CACHE_KEY}_${tenancyId}`;
    const data = localStorage.getItem(cacheKey);
    if (!data) return [];

    const parsed = JSON.parse(data);
    // Check if cache is expired (24 hours)
    const now = new Date().getTime();
    if (now - parsed.timestamp > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(cacheKey);
      return [];
    }

    // We need to recreate the icon components since they can't be serialized
    const items: RegisterItem[] = [];
    for (const item of parsed.items) {
      const iconKey = Object.keys(registerIconMap).find(key =>
        item.url.includes(key)
      );

      items.push({
        ...item,
        icon: createIconComponent(iconKey ? registerIconMap[iconKey] : faCabinetFiling)
      });
    }

    return items;
  } catch {
    return [];
  }
}

export function saveCachedRegisterItems(tenancyId: string, items: RegisterItem[]): void {
  try {
    const cacheKey = `${REGISTER_ITEMS_CACHE_KEY}_${tenancyId}`;
    // We need to serialize the items without the icon components
    const serializedItems = items.map(item => ({
      title: item.title,
      url: item.url,
      subtitle: item.subtitle,
      routeName: item.routeName, // Preserve routeName when serializing
    }));

    const data = {
      items: serializedItems,
      timestamp: new Date().getTime()
    };

    localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving register items to cache:', error);
  }
}
